import { useState } from "react";
import { X, LoaderCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BRANCH_OPTIONS = [
  { value: "chapter-1", label: "F12 Fitness Chapter 1 – Nalanchira" },
  { value: "chapter-2", label: "F12 Fitness Chapter 2 – Kudappanakunnu" },
];

export default function EnquiryModal({ open, initialBranch, onClose }) {
  const [form, setForm] = useState({ full_name: "", mobile: "", branch: initialBranch || "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const validate = () => {
    const errs = {};
    if (form.full_name.trim().length < 2) errs.full_name = "Please enter your full name.";
    const cleaned = form.mobile.replace(/[\s\-()]/g, "");
    if (!/^\+?\d{9,15}$/.test(cleaned)) errs.mobile = "Please enter a valid mobile number.";
    if (!form.branch) errs.branch = "Please select your preferred branch.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: form.full_name.trim(),
          mobile: form.mobile.trim(),
          branch: form.branch,
        }),
      });
      if (res.ok) {
        toast.success(
          "Thank you for contacting F12 Fitness! Your enquiry has been received. Our team will call you back shortly."
        );
        setForm({ full_name: "", mobile: "", branch: "" });
        setErrors({});
        onClose();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(
          data.detail ||
            "We couldn't send your enquiry right now. Please try again or call us directly."
        );
      }
    } catch {
      toast.error("We couldn't send your enquiry right now. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="enquiry-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
    >
      <div className="bg-[#141414] border border-[#FF5A1F]/40 rounded-xl max-w-md w-full p-6 sm:p-8 shadow-[0_0_50px_rgba(255,90,31,0.2)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-3xl tracking-wider text-white uppercase">
              Contact <span className="text-[#FF5A1F]">Us</span>
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-[#D1D1D1]">
              Interested in joining F12 Fitness? Select your preferred branch and leave your
              contact details. Our team will call you back.
            </p>
          </div>
          <button
            data-testid="enquiry-close-button"
            onClick={onClose}
            disabled={submitting}
            className="w-9 h-9 shrink-0 rounded-full bg-[#0A0A0A] border border-white/20 flex items-center justify-center text-white hover:text-[#FF5A1F] transition-colors disabled:opacity-50"
            aria-label="Close enquiry form"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
          <div>
            <label htmlFor="enquiry-name" className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#8E8E93] mb-1.5">
              Full Name *
            </label>
            <input
              id="enquiry-name"
              data-testid="enquiry-name-input"
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="Your full name"
              className="w-full bg-[#0A0A0A] border border-white/15 focus:border-[#FF5A1F] rounded-md px-4 py-3 text-sm text-white placeholder:text-[#8E8E93] outline-none transition-colors"
            />
            {errors.full_name && (
              <p data-testid="enquiry-name-error" className="mt-1.5 text-xs text-[#FF5A1F]">{errors.full_name}</p>
            )}
          </div>
          <div>
            <label htmlFor="enquiry-mobile" className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#8E8E93] mb-1.5">
              Mobile Number *
            </label>
            <input
              id="enquiry-mobile"
              data-testid="enquiry-mobile-input"
              type="tel"
              value={form.mobile}
              onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              placeholder="Your mobile number"
              className="w-full bg-[#0A0A0A] border border-white/15 focus:border-[#FF5A1F] rounded-md px-4 py-3 text-sm text-white placeholder:text-[#8E8E93] outline-none transition-colors"
            />
            {errors.mobile && (
              <p data-testid="enquiry-mobile-error" className="mt-1.5 text-xs text-[#FF5A1F]">{errors.mobile}</p>
            )}
          </div>
          <div>
            <label htmlFor="enquiry-branch" className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#8E8E93] mb-1.5">
              Select Your Branch *
            </label>
            <select
              id="enquiry-branch"
              data-testid="enquiry-branch-select"
              value={form.branch}
              onChange={(e) => setForm({ ...form, branch: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/15 focus:border-[#FF5A1F] rounded-md px-4 py-3 text-sm text-white outline-none transition-colors"
            >
              <option value="" disabled>
                Select your branch
              </option>
              {BRANCH_OPTIONS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
            {errors.branch && (
              <p data-testid="enquiry-branch-error" className="mt-1.5 text-xs text-[#FF5A1F]">{errors.branch}</p>
            )}
          </div>
          <button
            data-testid="enquiry-submit-button"
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5A1F] hover:bg-[#E0480F] disabled:opacity-60 disabled:cursor-not-allowed text-white font-heading text-xl tracking-widest px-6 py-3.5 rounded-sm transition-colors shadow-[0_0_25px_rgba(255,90,31,0.35)]"
          >
            {submitting && <LoaderCircle className="w-5 h-5 animate-spin" />}
            {submitting ? "SENDING..." : "REQUEST A CALLBACK"}
          </button>
        </form>
      </div>
    </div>
  );
}
